/**
 * Pure localStorage helpers for the Studio media library cache.
 * Mirrors `pitches-cache.ts` — stale-while-revalidate for
 * `pitch_assets` rows the pitcher owns across all pitches.
 */

import type { MediaAsset } from "../lib/media";
import { filterCreatorLibraryAssets, isCreatorLibraryAsset } from "../lib/media";

export const CACHE_VERSION = 1 as const;
export const MAX_CACHED_MEDIA_ASSETS = 500;

export interface CachedMediaSnapshot {
  version: typeof CACHE_VERSION;
  savedAt: string;
  assets: MediaAsset[];
  cdnUrls: Record<string, string>;
}

export interface LoadedCachedMedia {
  assets: MediaAsset[];
  cdnUrls: Map<string, string>;
  savedAt: string;
}

export function cacheKeyForUser(creatorUserId: string): string {
  return `hp:studio:media:v${CACHE_VERSION}:${creatorUserId}`;
}

function assetSortKey(a: MediaAsset): number {
  const raw = a.uploadedAt ?? "";
  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
}

function capAssetList(list: MediaAsset[]): MediaAsset[] {
  if (list.length <= MAX_CACHED_MEDIA_ASSETS) return list;
  return [...list]
    .sort((a, b) => assetSortKey(b) - assetSortKey(a))
    .slice(0, MAX_CACHED_MEDIA_ASSETS);
}

/**
 * Additive merge keyed by `$id`. Prefer incoming fields; keep cached
 * `cdnUrl` when the fetch omitted it.
 */
export function mergeMediaAssets(
  existing: MediaAsset[],
  incoming: MediaAsset[],
): MediaAsset[] {
  if (incoming.length === 0) return existing;
  const incomingById = new Map<string, MediaAsset>();
  for (const a of incoming) {
    if (a.$id && isCreatorLibraryAsset(a)) incomingById.set(a.$id, a);
  }
  const updated = existing.map((a) => {
    if (!a.$id) return a;
    const fresh = incomingById.get(a.$id);
    if (!fresh) return a;
    const merged: MediaAsset = { ...fresh };
    if (!merged.cdnUrl && a.cdnUrl) merged.cdnUrl = a.cdnUrl;
    return merged;
  });
  const existingIds = new Set(updated.map((a) => a.$id).filter(Boolean));
  const added = incoming.filter(
    (a) => a.$id && !existingIds.has(a.$id) && isCreatorLibraryAsset(a),
  );
  return capAssetList(filterCreatorLibraryAssets([...added, ...updated]));
}

export function loadCachedMedia(creatorUserId: string): LoadedCachedMedia | null {
  if (!creatorUserId || typeof localStorage === "undefined") return null;
  const key = cacheKeyForUser(creatorUserId);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CachedMediaSnapshot>;
    if (parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }
    if (!Array.isArray(parsed.assets)) {
      localStorage.removeItem(key);
      return null;
    }
    const cdnObj =
      parsed.cdnUrls && typeof parsed.cdnUrls === "object" ? parsed.cdnUrls : {};
    return {
      assets: filterCreatorLibraryAssets(parsed.assets as MediaAsset[]),
      cdnUrls: new Map(Object.entries(cdnObj)),
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : "",
    };
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function saveCachedMedia(
  creatorUserId: string,
  assets: MediaAsset[],
  cdnUrls: Map<string, string>,
): void {
  if (!creatorUserId || typeof localStorage === "undefined") return;
  const envelope: CachedMediaSnapshot = {
    version: CACHE_VERSION,
    savedAt: new Date().toISOString(),
    assets: capAssetList(assets),
    cdnUrls: Object.fromEntries(cdnUrls),
  };
  try {
    localStorage.setItem(cacheKeyForUser(creatorUserId), JSON.stringify(envelope));
  } catch {
    // Safari private mode / quota — non-fatal.
  }
}

export function clearCachedMedia(creatorUserId: string | null | undefined): void {
  if (!creatorUserId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(cacheKeyForUser(creatorUserId));
  } catch {
    // non-fatal
  }
}
